import React, { useState, useEffect, useTransition } from 'react';
import {
  Box,
  Typography,
  Paper,
  Slider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Stack,
  Alert,
  ToggleButton,
  ToggleButtonGroup,
  Button
} from '@mui/material';
import {
  Add as AddIcon,
  Remove as RemoveIcon,
  CloudUpload as SyncIcon
} from '@mui/icons-material';
import { SidebarComponentProps } from '../../types/PluginInterfaces';
import { VirtualRoomPainterPlugin } from './VirtualRoomPainterPlugin';
import { useVirtualRoomManager } from './hooks/useVirtualRoomManager';
import { RoomList } from './components/RoomList';
import { RoomCreationDialog } from './components/RoomCreationDialog';
import { SpawnPointSection } from './components/SpawnPointSection';
import { VirtualRoom } from './services/VirtualRoomStorage';
import { tileSyncManager } from './services/TileSyncManager';
import { currentMapId } from '../../../../../utils/currentMapId';
import { SyncStatusIndicator } from './components/SyncStatusIndicator';
import { TileSyncStatusIndicator } from './components/TileSyncStatusIndicator';
import { databaseBridge } from './services/DatabaseBridge';

interface VirtualRoomPainterSidebarProps extends SidebarComponentProps {
  plugin: VirtualRoomPainterPlugin;
}

export const VirtualRoomPainterSidebar: React.FC<VirtualRoomPainterSidebarProps> = ({
  plugin,
  context,
}) => {
  const [brushSize, setBrushSize] = useState(1);
  const [brushShape, setBrushShape] = useState<'square' | 'circle'>('square');
  const [pluginState, setPluginState] = useState(plugin.getPluginState());
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRoom, setEditingRoom] = useState<VirtualRoom | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editMode, setEditMode] = useState<'add' | 'delete' | 'placePoint'>('add');
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncStatus, setSyncStatus] = useState(databaseBridge.getSyncStatus());
  const [tilesSaved, setTilesSaved] = useState(tileSyncManager.getSyncStatus().savedThisSession);

  const [, startTransition] = useTransition();

  // The editor room's map (SC-422): the editor shows and saves that map's VRs.
  const editorMapId = currentMapId() ?? '';

  // Room management hook
  const {
    rooms,
    activeRoom,
    loading: roomsLoading,
    error: roomsError,
    createRoom,
    updateRoom,
    forgetRoom,
    activateRoom,
    refreshRooms,
    clearError
  } = useVirtualRoomManager({ mapId: editorMapId });

  // Update plugin state when it changes
  useEffect(() => {
    const updateState = () => {
      setPluginState(plugin.getPluginState());
    };

    plugin.on('stateChanged', updateState);
    return () => {
      plugin.off('stateChanged', updateState);
    };
  }, [plugin]);

  // Painted tiles wait up to 30 s for their save; leaving the painter must not drop them. Not
  // hurried: nothing bounds this wait and nothing is cleared after it (a tile that fails stays
  // queued), so the retries keep their pauses. Closing the editor disposes the plugin, and that
  // flush does hurry.
  useEffect(() => {
    return () => {
      tileSyncManager.flush().catch((error) => console.error('[VirtualRoomPainterSidebar] Saving tiles on unmount failed:', error));
    };
  }, []);

  // Update sync status periodically
  useEffect(() => {
    const updateSyncStatus = () => {
      setSyncStatus(databaseBridge.getSyncStatus());
      setTilesSaved(tileSyncManager.getSyncStatus().savedThisSession);
    };

    // Update sync status every 2 seconds
    const interval = setInterval(updateSyncStatus, 2000);
    
    return () => clearInterval(interval);
  }, []);

  // Update plugin when active room changes
  useEffect(() => {
    if (activeRoom) {
      console.log('[VirtualRoomPainterSidebar] Active room changed:', activeRoom);
      // Update plugin with current room for painting
      plugin.setCurrentRoom(activeRoom);
    }
  }, [activeRoom, plugin]);

  // Render preserved room borders when rooms are loaded
  useEffect(() => {
    if (rooms.length > 0 && !roomsLoading) {
      console.log('[VirtualRoomPainterSidebar] Rooms loaded, rendering preserved borders for', rooms.length, 'rooms');
      // Load room outlines from stored data and render them
      plugin.loadRoomOutlinesFromStoredData(rooms).catch(error => {
        console.error('[VirtualRoomPainterSidebar] Failed to load room outlines:', error);
      });
    }
  }, [rooms, roomsLoading, plugin]);

  // Listen for plugin events
  useEffect(() => {
    const handleEditModeChanged = () => {
      setEditMode(plugin.getEditMode());
    };

    const handleRoomDeleted = () => {
      // Rooms are managed by the hook, no need to manually refresh
    };

    const handleRoomPropertiesUpdated = async ({ roomId, room }: { roomId: string; room: VirtualRoom }) => {
      try {
        console.log('[VirtualRoomPainterSidebar] Received room properties update event:', roomId, room);
        
        // Update room in storage with all updated properties
        await updateRoom(roomId, room);
        console.log('[VirtualRoomPainterSidebar] Room properties updated in storage:', roomId, room);
        
        // Update the active room if it's the one being edited
        if (activeRoom?.id === roomId) {
          await activateRoom(room);
        }
      } catch (error) {
        console.error('Failed to update room properties:', error);
      }
    };

    const handleRoomOutlineUpdated = async ({ roomId, outlineData }: { roomId: string; outlineData: any }) => {
      try {
        // Update room in storage with outline data
        await updateRoom(roomId, { outlineData });
        console.log('[VirtualRoomPainterSidebar] Updated room outline data for:', roomId);
      } catch (error) {
        console.error('Failed to update room outline data:', error);
      }
    };

    // The bridge already stored the spawn point; reload so the list drops "No spawn point".
    const handleSpawnPointCreated = () => {
      refreshRooms();
    };

    plugin.on('editModeChanged', handleEditModeChanged);
    plugin.on('spawnPointCreated', handleSpawnPointCreated);
    plugin.on('roomDeleted', handleRoomDeleted);
    plugin.on('roomPropertiesUpdated', handleRoomPropertiesUpdated);
    plugin.on('roomOutlineUpdated', handleRoomOutlineUpdated);

    return () => {
      plugin.off('editModeChanged', handleEditModeChanged);
      plugin.off('spawnPointCreated', handleSpawnPointCreated);
      plugin.off('roomDeleted', handleRoomDeleted);
      plugin.off('roomPropertiesUpdated', handleRoomPropertiesUpdated);
      plugin.off('roomOutlineUpdated', handleRoomOutlineUpdated);
    };
  }, [plugin]);

  const handleBrushSizeChange = (event: Event, newValue: number | number[]) => {
    const size = newValue as number;
    setBrushSize(size);
    plugin.updateBrushSettings(size, brushShape);
  };

  const handleBrushShapeChange = (event: any) => {
    const shape = event.target.value as 'square' | 'circle';
    setBrushShape(shape);
    plugin.updateBrushSettings(brushSize, shape);
  };

  // Room management handlers
  const handleCreateRoom = () => {
    setEditingRoom(null);
    setDialogOpen(true);
  };

  const handleEditRoom = (room: VirtualRoom) => {
    setEditingRoom(room);
    setDialogOpen(true);
  };

  const handleRoomCreated = async (room: VirtualRoom) => {
    setIsSubmitting(true);
    try {
      const created = await createRoom(room);

      // Immediately activate newly created room so user can paint
      await activateRoom(created);

      // Inform plugin about new active room for painting
      plugin.setCurrentRoom(created);

      // Ensure plugin is activated
      if (!plugin.getPluginState().isActive) {
        await plugin.activate();
      }
    } catch (error) {
      console.error('Failed to create room:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRoomUpdated = async (room: VirtualRoom) => {
    setIsSubmitting(true);
    try {
      await updateRoom(room.id, room);
      
      // Update the plugin's current room if it's the one being edited
      if (activeRoom?.id === room.id) {
        plugin.setCurrentRoom(room);
      }
      
      console.log('[VirtualRoomPainterSidebar] Room updated successfully:', room.id);
    } catch (error) {
      console.error('Failed to update room:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRoomSelect = async (room: VirtualRoom) => {
    try {
      // Check if this room is already active and in edit mode
      if (activeRoom?.id === room.id && plugin.isInEditMode()) {
        console.log('[VirtualRoomPainterSidebar] Room already active, disabling edit mode');
        await plugin.disableEditMode();
        // Deactivate the room in the UI
        await activateRoom(null);
        return;
      }

      await activateRoom(room);
      // Update plugin with current room for painting
      plugin.setCurrentRoom(room);
      
      // Ensure plugin is activated for painting
      if (!plugin.getPluginState().isActive) {
        console.log('[VirtualRoomPainterSidebar] Activating plugin for painting');
        await plugin.activate();
      }
    } catch (error) {
      console.error('Failed to activate room:', error);
    }
  };

  // One Cloud Code call; a refused delete (e.g. upcoming bookings) keeps the VR and says why.
  const handleRoomDelete = async (roomId: string) => {
    try {
      await plugin.deleteRoom(roomId);
      forgetRoom(roomId);
      setSyncStatus(databaseBridge.getSyncStatus());
    } catch (error) {
      context.notificationService?.show(
        `Could not delete the room: ${error instanceof Error ? error.message : 'Unknown error'}`,
        'error'
      );
    }
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setEditingRoom(null);
    setIsSubmitting(false);
  };

  // Handle edit mode change (transition keeps map interactions smooth)
  const handleEditModeChange = (event: React.MouseEvent<HTMLElement>, newMode: 'add' | 'delete' | null) => {
    if (newMode !== null) {
      startTransition(() => {
        setEditMode(newMode);
        plugin.setEditMode(newMode);
      });
    }
  };

  // Handle manual sync to database
  const handleManualSync = async () => {
    setIsSyncing(true);
    try {
      console.log('[VirtualRoomPainterSidebar] Starting manual sync to database...');
      const result = await databaseBridge.manualSync();
      
      if (result.success) {
        console.log('[VirtualRoomPainterSidebar] Manual sync successful:', result);
        // Show success notification
        if (context.notificationService) {
          context.notificationService.show(
            `Successfully synced ${result.syncedCount} changes to database`, 
            'success'
          );
        }
      } else {
        console.error('[VirtualRoomPainterSidebar] Manual sync failed:', result);
        // Show error notification
        if (context.notificationService) {
          context.notificationService.show(
            `Sync failed: ${result.errors.join(', ')}`, 
            'error'
          );
        }
      }
      
      // Update sync status
      setSyncStatus(databaseBridge.getSyncStatus());
    } catch (error) {
      console.error('[VirtualRoomPainterSidebar] Manual sync error:', error);
      if (context.notificationService) {
        context.notificationService.show(
          `Sync error: ${error instanceof Error ? error.message : 'Unknown error'}`, 
          'error'
        );
      }
    } finally {
      setIsSyncing(false);
    }
  };

  return (
    <>
      <Paper elevation={2} sx={{ p: 2, height: '100%', overflow: 'auto' }}>
        <Typography variant="h6" gutterBottom>
          Virtual Room Painter
        </Typography>

        {(syncStatus.savedThisSession || tilesSaved) && (
          <Alert severity="info" sx={{ mb: 2 }} data-testid="vr-restart-notice">
            Saved. Rooms already running on this map get these changes only after they restart, from
            Platform Admin → Maps. Restarting disconnects their players.
          </Alert>
        )}

        {/* Database Sync Status */}
        <Box sx={{ mb: 2 }}>
          <SyncStatusIndicator compact={false} showDetails={true} />
        </Box>

        {/* Manual Sync Button */}
        <Box sx={{ mb: 2 }}>
          <Button
            variant="contained"
            startIcon={<SyncIcon />}
            onClick={handleManualSync}
            disabled={isSyncing || syncStatus.pendingChanges === 0}
            fullWidth
            size="small"
            sx={{
              backgroundColor: syncStatus.pendingChanges > 0 ? 'warning.main' : 'success.main',
              '&:hover': {
                backgroundColor: syncStatus.pendingChanges > 0 ? 'warning.dark' : 'success.dark',
              }
            }}
          >
            {isSyncing ? 'Syncing...' : 
             syncStatus.pendingChanges > 0 ? 
             `Sync ${syncStatus.pendingChanges} Changes` : 
             'All Changes Synced'}
          </Button>
          {syncStatus.pendingChanges > 0 && (
            <Typography variant="caption" color="warning.main" display="block" sx={{ mt: 1 }}>
              {syncStatus.pendingChanges} changes pending sync to database
            </Typography>
          )}
        </Box>

        {/* Tile Sync Status */}
        <Box sx={{ mb: 2 }}>
          <TileSyncStatusIndicator compact={false} showDetails={true} />
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Room Management */}
        <Typography variant="subtitle2" gutterBottom>
          Room Management
        </Typography>
        
        {roomsError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={clearError}>
            {roomsError}
          </Alert>
        )}

        <RoomList
          rooms={rooms}
          activeRoomId={plugin.isInEditMode() ? activeRoom?.id || null : null}
          selectedRoomId={activeRoom?.id || null}
          loading={roomsLoading}
          error={roomsError}
          onRoomSelect={handleRoomSelect}
          onRoomEdit={handleEditRoom}
          onRoomDelete={handleRoomDelete}
          onRoomCreate={handleCreateRoom}
          renderSelectedDetails={(room) => <SpawnPointSection plugin={plugin} room={room} />}
        />

        <Divider sx={{ my: 2 }} />

        {/* Brush Settings */}
        <Typography variant="subtitle2" gutterBottom>
          Brush Settings
        </Typography>

        {/* Brush Size */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" gutterBottom>
            Brush Size: {brushSize}
          </Typography>
          <Slider
            value={brushSize}
            onChange={handleBrushSizeChange}
            min={1}
            max={20}
            marks
            valueLabelDisplay="auto"
            disabled={!plugin.isInEditMode()}
          />
        </Box>

        {/* Brush Shape */}
        <Box sx={{ mb: 2 }}>
          <FormControl fullWidth size="small" disabled={!plugin.isInEditMode()}>
            <InputLabel>Brush Shape</InputLabel>
            <Select
              value={brushShape}
              label="Brush Shape"
              onChange={handleBrushShapeChange}
            >
              <MenuItem value="square">Square</MenuItem>
              <MenuItem value="circle">Circle</MenuItem>
            </Select>
          </FormControl>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Edit Mode Toggle */}
        <Typography variant="subtitle2" gutterBottom>
          Edit Mode
        </Typography>
        
        <Box sx={{ mb: 2 }}>
          <ToggleButtonGroup
            value={editMode}
            exclusive
            onChange={handleEditModeChange}
            disabled={!plugin.isInEditMode()}
            size="small"
            sx={{ width: '100%' }}
          >
            <ToggleButton value="add" sx={{ flex: 1 }}>
              <AddIcon sx={{ mr: 1 }} />
              Add Tiles
            </ToggleButton>
            <ToggleButton value="delete" sx={{ flex: 1 }}>
              <RemoveIcon sx={{ mr: 1 }} />
              Delete Tiles
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Room Data Visualization */}
        <Typography variant="subtitle2" gutterBottom>
          Room Data
        </Typography>

        <Box sx={{ mb: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1, border: 1, borderColor: 'divider' }}>
          <Typography variant="caption" color="text.secondary" display="block">
            Current Map Context
          </Typography>
          <Typography variant="body2" fontWeight="bold" color="primary">
            {editorMapId}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {rooms.length} rooms stored for this map
          </Typography>
        </Box>

        {/* Room Statistics */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Total Rooms: {rooms.length}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Active Room: {activeRoom ? activeRoom.name : 'None'}
          </Typography>
          {activeRoom && (
            <Typography variant="body2" color="text.secondary">
              Painted Tiles: {plugin.getCurrentRoomData().tileCount}
            </Typography>
          )}
        </Box>

        {/* All Rooms Summary */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            All Rooms:
          </Typography>
          {(() => {
            const allRooms = plugin.getAllRoomData();
            return allRooms.map(roomData => (
              <Typography key={roomData.roomId} variant="caption" color="text.secondary" display="block">
                {roomData.roomId}: {roomData.tileCount} tiles
              </Typography>
            ));
          })()}
        </Box>

        {/* Debug Information */}
        <Typography variant="subtitle2" gutterBottom>
          Debug Information
        </Typography>
        
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Plugin Active: {pluginState.isActive ? 'Yes' : 'No'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Plugin Room: {plugin.getCurrentRoom()?.name || 'None'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Is Painting: {pluginState.isPainting ? 'Yes' : 'No'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Brush Size: {pluginState.brushSize} | Shape: {pluginState.brushShape}
          </Typography>
          
          {/* Debug Actions */}
          <Box sx={{ mt: 2 }}>
            <Typography variant="caption" color="text.secondary" display="block">
              Debug Actions:
            </Typography>
            <Typography 
              variant="caption" 
              color="primary" 
              sx={{ cursor: 'pointer', textDecoration: 'underline' }}
              onClick={() => plugin.debugRegistration()}
            >
              Debug Registration
            </Typography>
            {' | '}
            <Typography 
              variant="caption" 
              color="primary" 
              sx={{ cursor: 'pointer', textDecoration: 'underline' }}
              onClick={() => plugin.testPainting(41, 23)}
            >
              Test Paint (41,23)
            </Typography>
          </Box>
        </Box>

        {/* Last Position */}
        {pluginState.lastPosition && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Last Position: ({pluginState.lastPosition.x}, {pluginState.lastPosition.y})
            </Typography>
          </Box>
        )}
      </Paper>

      {/* Room Creation/Edit Dialog */}
      <RoomCreationDialog
        open={dialogOpen}
        onClose={handleDialogClose}
        onRoomCreated={handleRoomCreated}
        onRoomUpdated={handleRoomUpdated}
        editingRoom={editingRoom}
        isSubmitting={isSubmitting}
      />

    </>
  );
};

export default VirtualRoomPainterSidebar; 